import React, { useRef, useEffect, useState } from 'react';
import { GridEditorState, GridEditorActions } from './GridEditorPresenter';
import { EditRightsRevokedBanner } from './components/EditRightsRevokedBanner';
import { Sidebar } from './components/Sidebar';
import { RightPropertiesPanel } from './components/sidebar/RightPropertiesPanel';
import { Toolbar } from './components/canvas/Toolbar';
import { GridCanvas, GridCanvasRef } from './components/canvas/GridCanvas';
import { useMeterRealtimeMetrics } from './hooks/useMeterRealtimeMetrics';
import { useAllMetersLiveMetrics } from './hooks/useAllMetersLiveMetrics';
import { useAllLuminairesLiveMetrics } from './hooks/useAllLuminairesLiveMetrics';

interface GridEditorViewProps {
  state: GridEditorState;
  actions: GridEditorActions;
  lightTypes: Array<{ id: string; label: string; power: number }>;
  svgRef: React.RefObject<SVGSVGElement>;
}

export const GridEditorView: React.FC<GridEditorViewProps> = ({
  state,
  actions,
  lightTypes,
  svgRef
}) => {
  const gridCanvasRef = useRef<GridCanvasRef>(null);
  const hasAutoFitRef = useRef(false);
  const meterMetrics = useMeterRealtimeMetrics(state.selectedElement);

  // Devices affichés comme marqueur individuel (dans la vue ET hors cluster).
  // On ne collecte les métriques live que pour ceux-là. null = pas encore connu.
  const [visibleDeviceIds, setVisibleDeviceIds] = useState<Set<string> | null>(null);

  const liveBubblesEnabled =
    state.metricsVisibility.voltagePower ||
    state.metricsVisibility.rssi ||
    state.metricsVisibility.statusRing;
  const { metrics: meterLiveMetrics } = useAllMetersLiveMetrics(
    state.elements,
    liveBubblesEnabled,
    visibleDeviceIds,
  );
  const { metrics: luminaireLiveMetrics } = useAllLuminairesLiveMetrics(
    state.elements,
    liveBubblesEnabled,
    visibleDeviceIds,
  );

  // Auto-center on elements when data first loads
  useEffect(() => {
    const elementsWithGps = state.elements.filter(el =>
      el.gpsCoordinates?.latitude &&
      el.gpsCoordinates?.longitude &&
      el.gpsCoordinates.latitude !== 0 &&
      el.gpsCoordinates.longitude !== 0
    );
    if (!hasAutoFitRef.current && elementsWithGps.length > 0) {
      hasAutoFitRef.current = true;
      setTimeout(() => {
        gridCanvasRef.current?.fitToElements(state.elements);
      }, 500);
    }
  }, [state.elements]);

  // Check if we're in initial loading state
  const isInitialLoading = (state.isLoadingNodes || state.isLoadingLinks || state.isLoadingStreetLights) &&
                          state.elements.length === 0 &&
                          state.connections.length === 0;

  return (
    <div
      className="w-full flex relative overflow-hidden"
      style={{ height: 'calc(100vh - 120px)' }}
    >
      {/* Initial loading overlay */}
      {isInitialLoading && (
        <div className="absolute inset-0 bg-gray-50 flex items-center justify-center z-40">
          <div className="text-center bg-white rounded-2xl p-12 shadow-2xl border border-slate-200 max-w-md">
            <div className="relative mb-8">
              <div className="w-20 h-20 mx-auto">
                <div className="absolute inset-0 border-4 border-slate-200 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-transparent border-t-blue-600 rounded-full animate-spin"></div>
                <div className="absolute inset-3 border-2 border-transparent border-t-green-500 rounded-full animate-spin" style={{animationDirection: 'reverse', animationDuration: '1.2s'}}></div>
                <div className="absolute inset-6 border-2 border-transparent border-t-yellow-400 rounded-full animate-spin" style={{animationDuration: '0.8s'}}></div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-800 mb-3">Initialisation du Grid Editor</h2>
            <p className="text-slate-600 mb-6">Chargement des données du plan électrique...</p>

            <div className="space-y-3 text-left">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${state.isLoadingNodes ? 'bg-blue-500 animate-pulse' : 'bg-slate-300'}`}></div>
                <span className={`text-sm ${state.isLoadingNodes ? 'text-blue-600 font-medium' : 'text-slate-500'}`}>
                  Chargement des noeuds électriques
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${state.isLoadingLinks ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`}></div>
                <span className={`text-sm ${state.isLoadingLinks ? 'text-green-600 font-medium' : 'text-slate-500'}`}>
                  Chargement des connexions
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${state.isLoadingStreetLights ? 'bg-yellow-500 animate-pulse' : 'bg-slate-300'}`}></div>
                <span className={`text-sm ${state.isLoadingStreetLights ? 'text-yellow-600 font-medium' : 'text-slate-500'}`}>
                  Chargement des éclairages
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Left Panel */}
      <Sidebar
        state={state}
        actions={actions}
      />

      {/* Main Canvas */}
      <div className="flex-1 flex flex-col min-w-0 bg-white">
        <EditRightsRevokedBanner count={state.unsavedChangesBlockedCount} />

        {/* Toolbar */}
        <Toolbar
          zoom={state.zoom}
          isSavingChanges={state.isSavingChanges}
          hasChanges={actions.hasChanges}
          saveErrors={state.saveErrors}
          lastSaveResult={state.lastSaveResult}
          isEditMode={state.isEditMode}
          searchQuery={state.searchQuery}
          saveProgress={state.saveProgress}
          actions={{
            setZoom: actions.setZoom,
            resetView: actions.resetView,
            saveChanges: actions.saveChanges,
            clearSaveErrors: actions.clearSaveErrors,
            fitMapToElements: actions.fitMapToElements,
            mergeElements: actions.mergeElements,
            toggleEditMode: actions.toggleEditMode,
            setSearchQuery: actions.setSearchQuery,
          }}
          gridCanvasRef={gridCanvasRef}
          onFitToElements={() => gridCanvasRef.current?.fitToElements(state.elements)}
        />

        {/* Grid Canvas */}
        <GridCanvas
          ref={gridCanvasRef}
          state={state}
          actions={actions}
          svgRef={svgRef}
          meterMetrics={meterMetrics}
          meterLiveMetrics={meterLiveMetrics}
          luminaireLiveMetrics={luminaireLiveMetrics}
          onVisibleDevicesChange={setVisibleDeviceIds}
        />
      </div>

      {/* Right Panel - Properties */}
      <RightPropertiesPanel
        selectedElement={state.selectedElement}
        selectedConnection={state.selectedConnection}
        meterMetrics={meterMetrics}
        lightTypes={lightTypes}
        actions={actions}
        isEditMode={state.isEditMode}
      />
    </div>
  );
};
